import React from 'react';
import styled from 'styled-components';
import { X, Headphones, Mail, Phone, ExternalLink } from 'lucide-react';
import { ThemeColors, Spacing, Typography, BorderRadius, Shadows, Transitions } from '../../styles/ThemeStyles';
import { SupportForm } from './SupportForm';
import { useAuth } from '../../contexts/AuthContext';

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
  padding: ${Spacing.lg};
  backdrop-filter: blur(2px);
`;

const ModalContainer = styled.div`
  background: white;
  border-radius: ${BorderRadius.lg};
  box-shadow: ${Shadows.xl};
  max-width: 600px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  position: relative;
  display: flex;
  flex-direction: column;
`;

const ModalHeader = styled.div`
  padding: ${Spacing.xl};
  border-bottom: 1px solid ${ThemeColors.border.light};
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: ${ThemeColors.gray[50]};
  border-radius: ${BorderRadius.lg} ${BorderRadius.lg} 0 0;
`;

const Title = styled.h2`
  font-size: ${Typography.fontSize.xl};
  font-weight: ${Typography.fontWeight.bold};
  color: ${ThemeColors.text.primary};
  margin: 0;
  display: flex;
  align-items: center;
  gap: ${Spacing.sm};
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: ${Spacing.xs};
  color: ${ThemeColors.text.secondary};
  border-radius: ${BorderRadius.sm};
  transition: all 0.2s ease;

  &:hover {
    background-color: ${ThemeColors.gray[200]};
    color: ${ThemeColors.text.primary};
  }
`;

const ModalBody = styled.div`
  padding: ${Spacing.xl};
`;

const ContactGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${Spacing.lg};
  margin-bottom: ${Spacing.xl};

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
`;

const ContactCard = styled.a`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: ${Spacing.lg};
  background-color: ${ThemeColors.gray[50]};
  border: 1px solid ${ThemeColors.border.light};
  border-radius: ${BorderRadius.md};
  text-decoration: none;
  color: ${ThemeColors.text.primary};
  transition: all ${Transitions.duration.short} ${Transitions.easing.easeInOut};

  &:hover {
    border-color: ${ThemeColors.primary.main};
    background-color: ${ThemeColors.primary.light}11;
    transform: translateY(-2px);
  }

  svg {
    margin-bottom: ${Spacing.sm};
    color: ${ThemeColors.primary.main};
  }
`;

const CardLabel = styled.span`
  font-size: ${Typography.fontSize.xs};
  color: ${ThemeColors.text.secondary};
  text-transform: uppercase;
  letter-spacing: 1px;
  margin-bottom: ${Spacing.xs};
`;

const CardValue = styled.span`
  font-weight: ${Typography.fontWeight.bold};
  font-size: ${Typography.fontSize.md};
`;

const FormSection = styled.div`
  border-top: 1px solid ${ThemeColors.border.light};
  padding-top: ${Spacing.xl};
`;

const SectionTitle = styled.h3`
  font-size: ${Typography.fontSize.lg};
  font-weight: ${Typography.fontWeight.semiBold};
  margin-bottom: ${Spacing.lg};
  color: ${ThemeColors.text.primary};
`;

interface SupportModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const SupportModal: React.FC<SupportModalProps> = ({ isOpen, onClose }) => {
  const { user } = useAuth();

  if (!isOpen) return null;

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <ModalOverlay onClick={handleOverlayClick}>
      <ModalContainer onClick={(e) => e.stopPropagation()}>
        <ModalHeader>
          <Title>
            <Headphones size={24} />
            Technical Support
          </Title>
          <CloseButton onClick={onClose}>
            <X size={24} />
          </CloseButton>
        </ModalHeader>

        <ModalBody>
          <ContactGrid>
            <ContactCard href="mailto:devbob.co@gmail.com">
              <Mail size={32} />
              <CardLabel>Email Us</CardLabel>
              <CardValue>devbob.co@gmail.com</CardValue>
              <InfoText style={{ marginTop: '4px' }}>
                <ExternalLink size={12} /> Click to compose
              </InfoText>
            </ContactCard>

            <ContactCard href="tel:5794235350">
              <Phone size={32} />
              <CardLabel>Call Us</CardLabel>
              <CardValue>579-423-5350</CardValue>
              <InfoText style={{ marginTop: '4px' }}>
                <ExternalLink size={12} /> Click to dial
              </InfoText>
            </ContactCard>
          </ContactGrid>

          <FormSection>
            <SectionTitle>Send a Message</SectionTitle>
            <SupportForm 
              userEmail={user?.email} 
              userName={user?.firstName ? `${user.firstName} ${user.lastName || ''}` : user?.username}
              onSuccess={() => setTimeout(onClose, 2000)}
            />
          </FormSection>
        </ModalBody>
      </ModalContainer>
    </ModalOverlay>
  );
};

const InfoText = styled.span`
  font-size: 10px;
  color: ${ThemeColors.text.secondary};
  display: flex;
  align-items: center;
  gap: 4px;
`;

